import React, { useEffect, useMemo, useState } from 'react';
import { MdDelete, MdAdd, MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import DeleteModel from '../Common/DeleteModel';
import WorkHistoryModal from './WorkHistoryModal';
import MilitaryExperienceModal from './MilitaryExperienceModal';
import { useAuth } from '../../Context/AuthContext';
import {
  getAllProviderWorkHistory,
  deleteProviderWorkHistory,
  saveProviderWorkHistory,
  getAllProviderMilitary,
  deleteProviderMilitary,
  saveProviderMilitary,
} from '../../Services/ProviderService';

const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const getWorkHistoryFromData = (data) => {
  const history = data?.workHistory ?? data?.work_history ?? data?.workHistories ?? [];
  return Array.isArray(history) ? history : [];
};

const getOrganizationIdFromStorage = () => {
  try {
    return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read organization ID from localStorage:', error);
    return null;
  }
};

const emptyMilitaryItem = {
  branchOfService: '',
  yearsOfService: '',
};

export default function WorkHistory({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [workHistory, setWorkHistory] = useState(() => getWorkHistoryFromData(initialData));
  const [militaryHistory, setMilitaryHistory] = useState(
    Array.isArray(initialData?.militaryHistory) ? initialData.militaryHistory : []
  );
  const [activeModal, setActiveModal] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingWorkHistory, setSavingWorkHistory] = useState(false);
  const [savingMilitaryHistory, setSavingMilitaryHistory] = useState(false);
  const [loadingMilitaryHistory, setLoadingMilitaryHistory] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, index: null, item: null, isLoading: false });

  useEffect(() => {
    setWorkHistory(getWorkHistoryFromData(initialData));
    setMilitaryHistory(Array.isArray(initialData?.militaryHistory) ? initialData.militaryHistory : []);
  }, [initialData]);

  const loadWorkHistory = async () => {
    if (!token || !providerId) return [];

    setLoading(true);
    try {
      const response = await getAllProviderWorkHistory(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setWorkHistory(refreshed);
        return refreshed;
      }else if (!response?.is_success && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        // toast.error(response?.message || 'Failed to load work history records');
      }
      return [];
    } catch (error) {
      console.error('Error loading provider work history:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadMilitaryHistory = async () => {
    if (!token || !providerId) return [];

    setLoadingMilitaryHistory(true);
    try {
      const response = await getAllProviderMilitary(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setMilitaryHistory(refreshed);
        return refreshed;
      }else if (!response?.is_success && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        // toast.error(response?.message || 'Failed to load military history records');
      }
      return [];
    } catch (error) {
      console.error('Error loading provider military history:', error);
      return [];
    } finally {
      setLoadingMilitaryHistory(false);
    }
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (getWorkHistoryFromData(initialData).length > 0) return;

    let isMounted = true;
    const fetchWorkHistory = async () => {
      if (!isMounted) return;
      await loadWorkHistory();
    };

    fetchWorkHistory();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  useEffect(() => {
    if (!token || !providerId) return;
    if (Array.isArray(initialData?.militaryHistory) && initialData.militaryHistory.length > 0) return;

    let isMounted = true;
    const fetchMilitaryHistory = async () => {
      if (!isMounted) return;
      await loadMilitaryHistory();
    };

    fetchMilitaryHistory();
    return () => {
      isMounted = false;
    };
  }, [token, providerId, initialData]);

  const historyFields = useMemo(
    () => ({
      work: [
        { name: 'previousEmployer', label: 'Previous Employer', placeholder: 'Enter previous employer' },
        { name: 'yearsOfExperience', label: 'Years of Experience', placeholder: 'Enter years of experience' },
      ],
      military: [
        { name: 'branchOfService', label: 'Branch of Service', placeholder: 'Enter branch of service' },
        { name: 'yearsOfService', label: 'Years of Service', placeholder: 'Enter years of service' },
      ],
    }),
    []
  );

  const openModal = (type, index = null) => {
    setActiveModal(type);
    setEditingIndex(index);
  };

  const closeModal = () => {
    setActiveModal(null);
    setEditingIndex(null);
  };

  const handleSaveHistory = async (values) => {
    if (activeModal === 'work') {
      const organizationId = getOrganizationIdFromStorage();
      const payload = {
        ...values,
        provider_id: providerId || values.provider_id,
        organization_id: organizationId || values.organization_id,
        type: values.id ? 'update' : 'create',
      };

      if (!token || !providerId) {
        const updated = [...workHistory];
        if (editingIndex !== null) {
          updated[editingIndex] = payload;
        } else {
          updated.push(payload);
        }
        setWorkHistory(updated);
        if (onSaveSuccess) onSaveSuccess({ workHistory: updated, militaryHistory });
        closeModal();
        toast.success(!token ? 'Work history saved locally.' : 'Work history saved locally until provider is created.');
        return;
      }

      setSavingWorkHistory(true);
      try {
        const response = await saveProviderWorkHistory(token, payload);
        if (response?.is_success) {
          const refreshed = await loadWorkHistory();
          if (!refreshed) {
            toast.error('Failed to refresh work history records after save');
            return;
          }
          closeModal();
          toast.success(editingIndex !== null ? 'Work history updated' : 'Work history added');
        } else if (!response?.is_success && response?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
        } else {
          toast.error(response?.message || 'Failed to save work history');
        }
      } catch (error) {
        console.error('Error saving provider work history:', error);
        toast.error(error?.message || 'Failed to save work history');
      } finally {
        setSavingWorkHistory(false);
      }
      return;
    }

    if (activeModal === 'military') {
      const organizationId = getOrganizationIdFromStorage();
      const payload = {
        ...values,
        provider_id: providerId || values.provider_id,
        organization_id: organizationId || values.organization_id,
        type: values.id ? 'update' : 'create',
      };

      if (!token || !providerId) {
        const updated = [...militaryHistory];
        if (editingIndex !== null) {
          updated[editingIndex] = payload;
        } else {
          updated.push(payload);
        }
        setMilitaryHistory(updated);
        if (onSaveSuccess) onSaveSuccess({ workHistory, militaryHistory: updated });
        closeModal();
        toast.success(!token ? 'Military experience saved locally.' : 'Military experience saved locally until provider is created.');
        return;
      }

      setSavingMilitaryHistory(true);
      try {
        const response = await saveProviderMilitary(token, payload);
        if (response?.is_success) {
          const refreshed = await loadMilitaryHistory();
          if (!refreshed) {
            toast.error('Failed to refresh military records after save');
            return;
          }
          closeModal();
          toast.success(editingIndex !== null ? 'Military experience updated' : 'Military experience added');
        } else if (!response?.is_success && response?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
        } else {
          toast.error(response?.message || 'Failed to save military experience');
        }
      } catch (error) {
        console.error('Error saving provider military experience:', error);
        toast.error(error?.message || 'Failed to save military experience');
      } finally {
        setSavingMilitaryHistory(false);
      }
      return;
    }

    closeModal();
  };

  const openDeleteModal = (type, index) => {
    setDeleteModal({ isOpen: true, type, index, item: type === 'work' ? workHistory[index] : militaryHistory[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, type: null, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.type === null || deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      if (deleteModal.type === 'work') {
        await handleDeleteWork(deleteModal.index);
      } else {
        await handleDeleteMilitary(deleteModal.index);
      }
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDeleteWork = async (index) => {
    const item = workHistory[index];
    const originalItems = [...workHistory];

    if (token && item && (item.id || item.Id || item.work_history_id)) {
      try {
        const id = item.id || item.Id || item.work_history_id;
        const response = await deleteProviderWorkHistory(token, id);
        if (response?.is_success) {
          const refreshed = await loadWorkHistory();
          const nextItems = Array.isArray(refreshed) ? refreshed : workHistory.filter((_, idx) => idx !== index);
          setWorkHistory(nextItems);
          if (onSaveSuccess) onSaveSuccess({ workHistory: nextItems, militaryHistory });
          return;
        } else if (response?.is_success === false && response?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
          setWorkHistory(originalItems);
          if (onSaveSuccess) onSaveSuccess({ workHistory: originalItems, militaryHistory });
          return;
        } else {
          toast.error(response?.message || 'Failed to delete work history');
          setWorkHistory(originalItems);
          if (onSaveSuccess) onSaveSuccess({ workHistory: originalItems, militaryHistory });
          return;
        }
      } catch (error) {
        console.error('Error deleting provider work history:', error);
        setWorkHistory(originalItems);
        if (onSaveSuccess) onSaveSuccess({ workHistory: originalItems, militaryHistory });
        return;
      }
    }
  };

  const handleDeleteMilitary = async (index) => {
    const item = militaryHistory[index];
    const originalItems = [...militaryHistory];

    if (token && item && (item.id || item.Id || item.military_experience_id)) {
      try {
        const id = item.id || item.Id || item.military_experience_id;
        const response = await deleteProviderMilitary(token, id);
        if (response?.is_success) {
          const refreshed = await loadMilitaryHistory();
          const nextItems = Array.isArray(refreshed) ? refreshed : militaryHistory.filter((_, idx) => idx !== index);
          setMilitaryHistory(nextItems);
          if (onSaveSuccess) onSaveSuccess({ workHistory, militaryHistory: nextItems });
          return;
        } else if (response?.is_success === false && response?.status_code === 500) {
          toast.error('Internal Server Error. Please Contact Support Team.');
          setMilitaryHistory(originalItems);
          if (onSaveSuccess) onSaveSuccess({ workHistory, militaryHistory: originalItems });
          return;
        }
        toast.error(response?.message || 'Failed to delete military experience');
        setMilitaryHistory(originalItems);
        if (onSaveSuccess) onSaveSuccess({ workHistory, militaryHistory: originalItems });
        return;
      } catch (error) {
        console.error('Error deleting provider military experience:', error);
        setMilitaryHistory(originalItems);
        if (onSaveSuccess) onSaveSuccess({ workHistory, militaryHistory: originalItems });
        return;
      }
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <div className="container-fluid p-0">
      <WorkHistoryModal
        isOpen={activeModal === 'work'}
        title={editingIndex !== null ? 'Edit Work History' : 'Add Work History'}
        fields={historyFields.work}
        initialData={editingIndex !== null ? workHistory[editingIndex] : null}
        onClose={closeModal}
        onSave={handleSaveHistory}
        isLoading={savingWorkHistory}
      />

      <MilitaryExperienceModal
        isOpen={activeModal === 'military'}
        title={editingIndex !== null ? 'Edit Military Experience' : 'Add Military Experience'}
        initialData={editingIndex !== null ? militaryHistory[editingIndex] : null}
        onClose={closeModal}
        onSave={handleSaveHistory}
        isLoading={savingMilitaryHistory}
      />

      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Work History</h6>
          <button
            type="button"
            className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
            onClick={() => openModal('work')}
          >
            <MdAdd size={18} />
            Add
          </button>
        </div>

    

        {loading ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : workHistory.length === 0 ? (
            <div className="text-center py-4 text-muted small">
              No work history records added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
            {
              workHistory.map((item, index) => (
                <React.Fragment key={item.id || item.Id || index}>
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Employer
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.employer_name || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                     Position
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.position_held || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                     Currently Employed
                    </div>

                    <div className="fw-semibold text-dark">
                        {item.is_current_employed === true ? 'Yes' : item.is_current_employed === false ? 'No' : item.is_currently_employed === true ? 'Yes' : item.is_currently_employed === false ? 'No' : 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Start Date
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.start_date || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button
                          type="button"
                          title="Edit"
                          aria-label="Edit"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={() => openModal('work', index)}
                        >
                          <MdEdit size={16} />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          aria-label="Delete"
                          className="btn btn-sm btn-danger rounded-3"
                          onClick={() => openDeleteModal('work', index)}
                        >
                          <MdDelete size={16} />
                        </button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}

      </div>



      
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Military Experience</h6>
          <button
            type="button"
            className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
            onClick={() => openModal('military')}
            // disabled={loadingMilitaryHistory}
          >
            <MdAdd size={18} />
            Add
          </button>
        </div>     

        {loadingMilitaryHistory ?(
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : militaryHistory.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No military records added yet. Click "Add" to create one.
              </div>
            ) : (
  
            <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
              {
                militaryHistory.map((item, index) => (
                  <React.Fragment key={index}>
                <div className="col">
                  <div className="card h-100 border-0 shadow-lg rounded-4">
                    <div className="card-body">
  
                      <div className="text-muted small mb-2">
                        Branch
                      </div>
  
                      <div className="fw-semibold text-dark">
                       {item.branch_name || item.branch || 'N/A'}
                      </div>
  
                    </div>
                  </div>
                </div>
  
  
    
                <div className="col">
                  <div className="card h-100 border-0 shadow-lg rounded-4">
                    <div className="card-body">
  
                      <div className="text-muted small mb-2">
                        Role
                      </div>
  
                      <div className="fw-semibold text-dark">
                       {item.role_name || item.role || 'N/A'}
                      </div>
  
                    </div>
                  </div>
                </div>
  
  
  
                <div className="col">
                  <div className="card h-100 border-0 shadow-lg rounded-4">
                    <div className="card-body">
  
                      <div className="text-muted small mb-2">
                        Start Date
                      </div>
  
                      <div className="fw-semibold text-dark">
                       {item.start_date || 'N/A'}
                      </div>
  
                    </div>
                  </div>
                </div>



                <div className="col">
                  <div className="card h-100 border-0 shadow-lg rounded-4">
                    <div className="card-body">
  
                      <div className="text-muted small mb-2">
                        End Date
                      </div>
  
                      <div className="fw-semibold text-dark">
                        {item.end_date || 'N/A'}
                      </div>
  
                    </div>
                  </div>
                </div>
  
  
  
                <div className="col">
                  <div className="card h-100 border-0 shadow-lg rounded-4">
                    <div className="card-body">
  
                      <div className="text-muted small mb-2">
                        Actions
                      </div>
  
                      <div className="d-flex gap-2">
  
                        <button
                          type="button"
                          title="Edit"
                          aria-label="Edit"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={() => openModal('military', index)}
                        >
                          <MdEdit size={16} />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          aria-label="Delete"
                          className="btn btn-sm btn-danger rounded-3"
                          onClick={() => openDeleteModal('military', index)}
                        >
                          <MdDelete size={16} />
                        </button>
  
                      </div>
  
                    </div>
                  </div>
                </div> 
                  </React.Fragment>
  
                ))}
                
  
              </div>
  
              )}
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel={deleteModal.type === 'military' ? 'military experience record' : 'work history record'}
        itemName={deleteModal.item ? (deleteModal.type === 'military'
          ? deleteModal.item.branch_name || deleteModal.item.branch || 'this military experience record'
          : deleteModal.item.employer_name || deleteModal.item.position_held || 'this work history record')
          : deleteModal.type === 'military' ? 'this military experience record' : 'this work history record'}
        description={deleteModal.type === 'military'
          ? 'This will permanently delete this military experience record. This action cannot be undone.'
          : 'This will permanently delete this work history record. This action cannot be undone.'}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
